<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, numeric, minLength, maxLength } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'

const { t } = useI18n()
const { umsokn } = useUmsokn()

const props = defineProps<{
  disable?: boolean
  loading?: boolean
  bankValidation?: { valid: boolean; errorMessage: string } | null
  runBankCheck?: (u: any) => Promise<{ valid: boolean; errorMessage?: string }>
}>()

const localBankError = ref('')
const showBankError = ref(false)

const rules = {
  umsokn: {
    bankNumber: { required, numeric, minLength: minLength(4), maxLength: maxLength(4) },
    ledger: { required, numeric },
    accountNumber: { required, numeric }
  }
}

// $autoDirty makes a field "touched" after user edits it
const v$ = useVuelidate(rules, { umsokn })

async function validate(): Promise<boolean> {
  localBankError.value = ''
  showBankError.value = false
  v$.value.$touch()
  if (v$.value.$invalid) return false

  if (props.runBankCheck) {
    const res = await props.runBankCheck(umsokn.value)
    if (!res.valid) {
      localBankError.value = res.errorMessage || t('umsokn_bankareikningur_villa')
      showBankError.value = true
      return false
    }
  }
  return true
}

defineExpose({ v$, validate })

// numeric-only sanitization
;['bankNumber', 'ledger', 'accountNumber'].forEach((k) => {
  watch(
    () => (umsokn.value as any)?.[k],
    (v) => {
      if (umsokn.value && v != null) (umsokn.value as any)[k] = String(v).replace(/[^\d]/g, '')
    }
  )
})

// ---- error message helpers ----
const bankNumberErrors = computed(() => {
  const f = v$.value.umsokn.bankNumber
  if (!f.$error) return []
  const msgs: string[] = []
  if (f.required.$invalid) msgs.push(t('lifeyrir_umsokn_step2_bankanumer_error'))
  if (f.minLength.$invalid || f.maxLength.$invalid) msgs.push(t('lifeyrir_umsokn_step2_bankanumer_error_length'))
  if (f.numeric.$invalid) msgs.push(t('lifeyrir_umsokn_step2_bankanumer_error_numeric'))
  return msgs
})

const ledgerErrors = computed(() => {
  const f = v$.value.umsokn.ledger
  if (!f.$error) return []
  const msgs: string[] = []
  if (f.required.$invalid) msgs.push(t('lifeyrir_umsokn_step2_hofudbok_error'))
  if (f.numeric.$invalid) msgs.push(t('lifeyrir_umsokn_step2_hofudbok_error_numeric'))
  return msgs
})

const accountNumberErrors = computed(() => {
  const f = v$.value.umsokn.accountNumber
  if (!f.$error) return []
  const msgs: string[] = []
  if (f.required.$invalid) msgs.push(t('lifeyrir_umsokn_step2_reikningsnumer_error_required'))
  if (f.numeric.$invalid) msgs.push(t('lifeyrir_umsokn_step2_reikningsnumer_error_numeric'))
  return msgs
})
</script>

<template>
  <div v-if="umsokn" class="col-sm-12">
    <v-row>
      <v-col>
        <h2 class="forms__header">
          {{ t('lifeyrir_umsokn_step2_header1') }}
        </h2>
      </v-col>
    </v-row>

    <v-row :class="{ loading: props.loading }">
      <v-col cols="12" md="4" lg="5">
        <v-text-field
          v-model="umsokn.bankNumber"
          :label="t('lifeyrir_umsokn_step2_bankanumer')"
          variant="outlined"
          density="comfortable"
          inputmode="numeric"
          :readonly="props.disable"
          hide-details="auto"
          :error="v$.umsokn.bankNumber.$error"
          :error-messages="bankNumberErrors"
        />
      </v-col>

      <v-col cols="12" md="4" lg="2">
        <v-text-field
          v-model="umsokn.ledger"
          :label="t('lifeyrir_umsokn_step2_hofudbok')"
          variant="outlined"
          density="comfortable"
          inputmode="numeric"
          :readonly="props.disable"
          hide-details="auto"
          :error="v$.umsokn.ledger.$error"
          :error-messages="ledgerErrors"
        />
      </v-col>

      <v-col cols="12" md="4" lg="5">
        <v-text-field
          v-model="umsokn.accountNumber"
          :label="t('lifeyrir_umsokn_step2_reikningsnumer')"
          variant="outlined"
          density="comfortable"
          inputmode="numeric"
          :readonly="props.disable"
          hide-details="auto"
          :error="v$.umsokn.accountNumber.$error"
          :error-messages="accountNumberErrors"
        />
      </v-col>
    </v-row>

    <v-row v-if="showBankError || (props.bankValidation && props.bankValidation.valid === false)">
      <v-col>
        <v-alert type="error" color="error" variant="flat" density="compact" class="mt-2" closable @click:close="showBankError = false">
          {{ localBankError || props.bankValidation?.errorMessage }}
        </v-alert>
      </v-col>
    </v-row>
  </div>
</template>
